import { CollectionParams, setDefaultSearchQueryParams } from 'utils/citySearchUtils';
import { LocalDateTime, LocalTime } from 'js-joda';
import { SearchMode, SearchQueryStringParams } from 'components/search/utils';
import { ServerSearchParams } from 'actions/AvailabilityActions';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';
import { getLocationSearchFilter } from 'routes/www/availability-search/getLocationSearchFilter';
import { getRealDateTimes } from 'routes/www/availability-search/getRealDateTimes';
import { isArray } from 'es-toolkit/compat';

export type SearchRequestParams = {
  query: ServerSearchParams;
  additionalQuery: consumer.IBusinessSearchFilter;
};

/**
 * Executes an availability search
 * Guards against past times, and executes the search action
 */
export function buildSearchRequestParams(
  searchType: SearchMode['stateType'],
  queryStringParams: SearchQueryStringParams,
  timeZone: string,
  collectionParams: CollectionParams = {
    collectionDate: undefined,
    collectionDateEnd: undefined,
    collectionId: undefined,
    collectionOfferingSearchType: undefined,
  }
): SearchRequestParams {
  const {
    date,
    dateEnd,
    time,
    size,
    type,
    q,
    userTimeZone,
    latlng,
    experienceTagAll,
    experienceTagDineIn,
    experienceTagToGo,
    experienceTagCampaign,
    experienceTagSeatingArea,
  } = setDefaultSearchQueryParams(queryStringParams, timeZone, collectionParams);

  const { realDateDateTime, realDateEndDateTime } = getRealDateTimes(date, time, timeZone, dateEnd);

  const location = getLocationSearchFilter(latlng, getBoundingBox(queryStringParams));

  const cuisineTag = isArray(queryStringParams.cuisines)
    ? queryStringParams.cuisines
    : queryStringParams.cuisines
      ? [queryStringParams.cuisines]
      : undefined;

  switch (searchType) {
    case 'collectionPage':
    case 'city': {
      return {
        query: {
          dateTime: realDateEndDateTime
            ? {
                min: LocalDateTime.of(realDateDateTime.toLocalDate(), LocalTime.MIN),
                max: LocalDateTime.of(realDateEndDateTime.toLocalDate(), LocalTime.MAX),
              }
            : {
                min: LocalDateTime.of(
                  realDateDateTime.toLocalDate(),
                  realDateDateTime.toLocalTime()
                ).minusMinutes(45),
                max: LocalDateTime.of(
                  realDateDateTime.toLocalDate(),
                  realDateDateTime.toLocalTime()
                ).plusMinutes(45),
              },
          size: size
            ? {
                min: size,
                max: size,
              }
            : undefined,
          location,
          type,
          q,
          userTimeZone,
          experienceTagAll,
          experienceTagDineIn,
          experienceTagToGo,
          experienceTagCampaign,
          experienceTagSeatingArea,
        },
        additionalQuery: {
          city: queryStringParams.city ? [queryStringParams.city] : undefined,
          businessType: queryStringParams.businessType
            ? [queryStringParams.businessType]
            : undefined,
          cuisineTag,
          priceRange: queryStringParams.priceRange ? [queryStringParams.priceRange] : undefined,
        },
      };
    }

    case 'group': {
      return {
        query: {
          dateTime: {
            min: LocalDateTime.of(
              realDateDateTime.toLocalDate(),
              realDateDateTime.toLocalTime()
            ).minusMinutes(45),
            max: LocalDateTime.of(
              realDateDateTime.toLocalDate(),
              realDateDateTime.toLocalTime()
            ).plusMinutes(45),
          },
          size: size
            ? {
                min: size,
                max: size,
              }
            : undefined,
          type,
          userTimeZone,
        },
        additionalQuery: {
          businessGroupId: queryStringParams.businessGroupId
            ? [Number(queryStringParams.businessGroupId)]
            : undefined,
          cuisineTag,
        },
      };
    }

    case 'togo': {
      return {
        query: {
          dateTime: queryStringParams.date
            ? {
                min: LocalDateTime.of(realDateDateTime.toLocalDate(), LocalTime.of(4, 0)),
                max: LocalDateTime.of(
                  realDateDateTime.toLocalDate().plusDays(1),
                  LocalTime.of(3, 59)
                ),
              }
            : undefined,
          type: 'ONLINE_ORDERS',
          userTimeZone,
          location,
        },
        additionalQuery: {
          city: queryStringParams.city ? [queryStringParams.city] : undefined,
          cuisineTag,
        },
      };
    }
    default:
      assertNever(searchType);
  }
}

const getBoundingBox = (
  queryStringParams: SearchQueryStringParams
): core.BoundingBox | undefined => {
  const { topLeftLat, topLeftLon, bottomRightLat, bottomRightLon } = queryStringParams;
  if (topLeftLat && topLeftLon && bottomRightLon && bottomRightLat) {
    const parsedTopLeftLat = parseFloat(topLeftLat);
    const parsedTopLeftLon = parseFloat(topLeftLon);
    const parsedBottomRightLat = parseFloat(bottomRightLat);
    const parsedBottomRightLon = parseFloat(bottomRightLon);

    if (
      isNaN(parsedTopLeftLat) ||
      isNaN(parsedTopLeftLon) ||
      isNaN(parsedBottomRightLat) ||
      isNaN(parsedBottomRightLon)
    ) {
      return;
    }

    return {
      topLeft: {
        latitude: parsedTopLeftLat,
        longitude: parsedTopLeftLon,
      },
      bottomRight: {
        latitude: parsedBottomRightLat,
        longitude: parsedBottomRightLon,
      },
    };
  }
  return undefined;
};
